import {
  ArrowDownward,
  Dataset,
  FilterAlt,
  SwapVert,
  ViewColumn,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { MutableRefObject, useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import {
  SavedReportSettings,
  SavedReportSettingsPrivacyType,
} from '~/data/reportSettings';
import {
  UseReportDatasetOutput,
  UseSavedReportSettingsConfigOutput,
} from '~/scenes/PortalModules/Analytics/common';
import {
  SavedSettingsUpdateState,
  useSaveReportSettingsDialog,
} from '~/scenes/PortalModules/Analytics/common/SavedReportSettingsSelector/SaveReportSettingsDialog/useSaveReportSettingsDialog';
import { useSxStyles } from '~/scenes/PortalModules/Analytics/common/SavedReportSettingsSelector/SaveReportSettingsDialog/useStyles';

type PropsType<P> = {
  open: boolean;
  onClose: () => void;
  selectedReportSettings?: SavedReportSettings<P>;
  apiRef: MutableRefObject<GridApiPremium>;
  reportDataset: UseReportDatasetOutput<P>;
  isEdit: boolean;
} & Pick<
  UseSavedReportSettingsConfigOutput<P>,
  | 'selectedReportSettings'
  | 'onCreateReportSettings'
  | 'onUpdateSelectedReportSettings'
>;

export const SavedReportSettingsDialog = <P,>(props: PropsType<P>) => {
  const { open, onClose, isEdit } = props;

  const sxStyles = useSxStyles();

  const { t } = useTranslation();

  const {
    access,
    form: {
      control,
      formState: { isDirty },
    },
    settingsSelectionState: { hasAllSettingsSelected },
    settingsChangeState,
    settingsUpdateState,
    isSubmitDisabled,
    onSubmit,
  } = useSaveReportSettingsDialog(props);

  const getIncludeSettingsTranslation = useCallback(
    (includeKey: string) => {
      switch (includeKey) {
        case 'reportSelection':
          return t('dataset');
        case 'columnVisibility':
          return t('laColumns');
        case 'columnSorting':
          return t('columnSorting');
        case 'columnFiltering':
          return t('columnFiltering');
        default:
          return '';
      }
    },
    [t],
  );

  const { includeChangedLabels } = useMemo(() => {
    const { isUpdated: _isUpdated, ...updatedStates } = settingsUpdateState;

    const changedStateKeys = Object.entries(updatedStates)
      .filter(([_key, value]) => value === SavedSettingsUpdateState.Changed)
      .map(([key]) => key);

    const includeChangedLabels = changedStateKeys.map(
      getIncludeSettingsTranslation,
    );

    return {
      includeChangedLabels,
    };
  }, [settingsUpdateState, getIncludeSettingsTranslation]);

  const showHasChanged = useCallback(
    (hasChanged: boolean) => {
      return hasChanged && isEdit ? (
        <Box sx={sxStyles.hasChanged}>{t('changed')}</Box>
      ) : null;
    },
    [t, sxStyles, isEdit],
  );

  const selectPrivacyType = useMemo(() => {
    const options = [
      {
        label: t('laPersonalReportSettings'),
        value: SavedReportSettingsPrivacyType.PERSONAL,
      },
      {
        label: t('shared'),
        value: SavedReportSettingsPrivacyType.SHARED,
      },
    ];

    return {
      options,
      labelId: 'privacy-type-selection',
      label: t('visibility'),
    };
  }, [t]);

  return (
    <DraggableDialog open={open} onClose={onClose}>
      <SolaForceDialogTitle onClose={onClose}>
        {isEdit ? t('laEdit') : t('laNew')}
      </SolaForceDialogTitle>
      <DialogContent dividers={true}>
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={access.canCreateShared ? 7 : 12}>
            <Controller
              control={control}
              name={'name'}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <TextField
                    value={value}
                    onChange={onChange}
                    size={'small'}
                    label={t('laTitle')}
                    required={true}
                    fullWidth={true}
                    error={!!error}
                    helperText={error?.message}
                  />
                );
              }}
            />
          </Grid>
          {access.canCreateShared && (
            <Grid item xs={5}>
              <Controller
                control={control}
                name={'privacyType'}
                render={({ field: { value, onChange } }) => {
                  return (
                    <FormControl variant={'outlined'} fullWidth={true}>
                      <InputLabel id={selectPrivacyType.labelId} size={'small'}>
                        {selectPrivacyType.label}
                      </InputLabel>
                      <Select
                        labelId={selectPrivacyType.labelId}
                        variant={'outlined'}
                        name={'privacyType'}
                        label={selectPrivacyType.label}
                        value={value}
                        onChange={onChange}
                        size={'small'}
                      >
                        {selectPrivacyType.options.map((option, index) => {
                          const { value, label } = option;

                          return (
                            <MenuItem
                              value={value}
                              key={`privacy-type-option-${index}`}
                            >
                              {label}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  );
                }}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Accordion defaultExpanded={!hasAllSettingsSelected}>
              <AccordionSummary
                expandIcon={<ArrowDownward />}
                sx={sxStyles.accordionComponent}
              >
                {t('advancedSettings')}
              </AccordionSummary>
              <Divider />
              <AccordionDetails sx={sxStyles.accordionComponent}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography variant={'subtitle1'}>
                      {t('includeSettings')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name={'savedSettings.reportSelection'}
                      render={({ field: { value, onChange } }) => {
                        return (
                          <FormControlLabel
                            control={<Switch />}
                            label={
                              <Box sx={sxStyles.label}>
                                <Dataset fontSize={'medium'} /> {t('dataset')}
                                {showHasChanged(
                                  settingsChangeState.reportSelection,
                                )}
                              </Box>
                            }
                            checked={value}
                            onChange={onChange}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name={'savedSettings.columnVisibility'}
                      render={({ field: { value, onChange } }) => {
                        return (
                          <FormControlLabel
                            control={<Switch />}
                            label={
                              <Box sx={sxStyles.label}>
                                <ViewColumn fontSize={'medium'} />{' '}
                                {t('laColumns')}
                                {showHasChanged(
                                  settingsChangeState.columnVisibility,
                                )}
                              </Box>
                            }
                            checked={value}
                            onChange={onChange}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name={'savedSettings.columnSorting'}
                      render={({ field: { value, onChange } }) => {
                        return (
                          <FormControlLabel
                            control={<Switch />}
                            label={
                              <Box sx={sxStyles.label}>
                                <SwapVert /> {t('columnSorting')}{' '}
                                {showHasChanged(
                                  settingsChangeState.columnSorting,
                                )}
                              </Box>
                            }
                            checked={value}
                            onChange={onChange}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name={'savedSettings.columnFiltering'}
                      render={({ field: { value, onChange } }) => {
                        return (
                          <FormControlLabel
                            control={<Switch />}
                            label={
                              <Box sx={sxStyles.label}>
                                <FilterAlt fontSize={'medium'} />{' '}
                                {t('columnFiltering')}
                                {showHasChanged(
                                  settingsChangeState.columnFiltering,
                                )}
                              </Box>
                            }
                            checked={value}
                            onChange={onChange}
                          />
                        );
                      }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
          {isEdit && (
            <Grid item xs={12}>
              {includeChangedLabels.length > 0 ? (
                <Alert severity={'info'}>{`${t(
                  'changesDetected',
                )}: ${includeChangedLabels.join(', ')}`}</Alert>
              ) : (
                !isDirty && (
                  <Alert severity={'error'}>{t('noChangesDetected')}</Alert>
                )
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('laCancel')}</Button>
        <Button onClick={onSubmit} disabled={isSubmitDisabled}>
          {t('laSave')}
        </Button>
      </DialogActions>
    </DraggableDialog>
  );
};
